import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { GlobalContext } from "@/helpers/contexts/contexts";
import { reportService } from "@/shared/services/reportService";
import { DRILL_LEVELS, FILTERS } from "@/config/constants";
import { isDateRangeOutOfBounds } from "@/features/campaigns/shared/helpers/drillUpDownHelper";
import { CircularProgress, Typography } from "@mui/material";
import { logger } from "@/helpers/loggerHelper";

type RangeDate = {
  year: string | number;
  month: string | number;
  date: string | number;
};

type ReportRange = {
  from: RangeDate;
  to: RangeDate;
};

type DrillValues = {
  filter?: string;
  customRange?: string[];
  group_id?: string | number;
  location_id?: string | number;
  channel_id?: string | number;
};

type DrillType = string;
type DrillDirection = "up" | "down";

type AuthUser = {
  id?: string | number;
  fk_role_id?: string | number;
  participantData?: {
    id?: string | number;
    system_generated_participant_id?: string | number;
  };
};

type DrillControlProps = {
  drillLevel: DrillType;
  onDrillChange: (_drillType: DrillType, _data: unknown[]) => void;
  widgetId: string | number;
  values: DrillValues;
  initialRange?: ReportRange | null;
  setDrillLevel?: (_drillLevel: DrillType) => void;
};

const DrillControl = ({
  drillLevel,
  onDrillChange,
  widgetId,
  values,
  initialRange,
  setDrillLevel,
}: DrillControlProps) => {
  const { campaignSysId } = useParams<{ campaignSysId?: string }>();
  const { selectedBrand, loggedInUser } = useContext(GlobalContext);
  const loggedInUserData = loggedInUser as AuthUser | null | undefined;
  const [isDrillDownDisabled, setIsDrillDownDisabled] = useState(false);
  const [isDrillUpDisabled, setIsDrillUpDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formatDate = useCallback(
    (year: string | number, month: string | number, day: string | number) =>
      `${year}-${month.toString().padStart(2, "0")}-${day
        .toString()
        .padStart(2, "0")}`,
    []
  );

  const formatRange = useCallback(
    (range?: ReportRange | null) => {
      if (range && "from" in range && "to" in range) {
        return {
          from: formatDate(range.from.year, range.from.month, range.from.date),
          to: formatDate(range.to.year, range.to.month, range.to.date),
        };
      }
      return null;
    },
    [formatDate]
  );

  useEffect(() => {
    setIsDrillUpDisabled(
      drillLevel === DRILL_LEVELS.YEAR ||
        (drillLevel === DRILL_LEVELS.QUARTER &&
          values.filter === FILTERS.LAST_QUARTER)
    );
  }, [drillLevel, values.filter]);

  useEffect(() => {
    if (setDrillLevel && drillLevel !== DRILL_LEVELS.MONTH) {
      setDrillLevel(DRILL_LEVELS.MONTH);
    }
  }, [values.filter, setDrillLevel]);

  useEffect(() => {
    if (
      values.filter === FILTERS.LAST_QUARTER &&
      drillLevel === DRILL_LEVELS.YEAR
    ) {
      if (typeof setDrillLevel === "function") {
        setDrillLevel(DRILL_LEVELS.MONTH);
      }
    }
  }, [values.filter, drillLevel, setDrillLevel]);

  useEffect(() => {
    setIsDrillDownDisabled(
      drillLevel === DRILL_LEVELS.DAY ||
        isDateRangeOutOfBounds(values, initialRange, drillLevel)
    );
  }, [values.filter, drillLevel, values.customRange, initialRange]);

  const getRequestData = useCallback(
    (drillType: DrillType) => {
      const formattedRange =
        values.filter === FILTERS.CUSTOM && values.customRange
          ? { from: values.customRange[0], to: values.customRange[1] }
          : formatRange(initialRange);

      const reqData = {
        system_generated_campaign_id: `${campaignSysId}`,
        fk_brand_id: selectedBrand?.id,
        fk_participant_id: loggedInUserData?.participantData?.id || 0,
        participant_system_id:
          loggedInUserData?.participantData?.system_generated_participant_id ||
          "",
        userId: loggedInUserData?.id,
        fk_role_id: loggedInUserData?.fk_role_id,
        group_id: values?.group_id || "",
        location_id: values?.location_id || "",
        channel_id: values?.channel_id || "",
        filter: values?.filter,
        widgetId: widgetId,
        drill_type: drillType,
        start_period: formattedRange?.from,
        end_period: formattedRange?.to,
      };
      return reqData;
    },
    [
      campaignSysId,
      initialRange,
      selectedBrand,
      loggedInUserData,
      values,
      formatRange,
      widgetId,
    ]
  );

  const handleDrillChange = useCallback(
    async (drillType: DrillType) => {
      setIsLoading(true);
      const reqData = getRequestData(drillType);
      try {
        const response = await reportService.getWidgetDrillUpDownData(reqData);
        const newData = response?.data?.data || [];
        onDrillChange(drillType, newData);
      } catch (error) {
        logger.error("handleDrillChange error", error);
      } finally {
        setIsLoading(false);
      }
    },
    [getRequestData, onDrillChange]
  );

  const handleDrill = useCallback(
    (direction: DrillDirection) => {
      let drillType: DrillType | null | undefined;
      switch (drillLevel) {
        case DRILL_LEVELS.YEAR:
          drillType = direction === "down" ? DRILL_LEVELS.QUARTER : null;
          break;
        case DRILL_LEVELS.QUARTER:
          drillType =
            direction === "down" ? DRILL_LEVELS.MONTH : DRILL_LEVELS.YEAR;
          break;
        case DRILL_LEVELS.MONTH:
          drillType =
            direction === "down" ? DRILL_LEVELS.DAY : DRILL_LEVELS.QUARTER;
          break;
        case DRILL_LEVELS.DAY:
          drillType = direction === "up" ? DRILL_LEVELS.MONTH : null;
          break;
        default:
          return;
      }
      if (drillType) {
        handleDrillChange(drillType);
      }
    },
    [drillLevel, handleDrillChange, values.filter]
  );

  const getCurrentDrillType = () => {
    switch (drillLevel) {
      case DRILL_LEVELS.YEAR:
        return "Year";
      case DRILL_LEVELS.QUARTER:
        return "Quarter";
      case DRILL_LEVELS.MONTH:
        return "Month";
      case DRILL_LEVELS.DAY:
        return "Day";
      default:
        return "";
    }
  };
  return (
    <>
      <div className="drill-button-container">
        {isLoading ? (
          <CircularProgress size={10} className="mui-loader" color="primary" />
        ) : (
          <Typography
            className="mui-loader fs-10"
            variant="caption"
            color="text.secondary"
          >
            {getCurrentDrillType()}
          </Typography>
        )}

        <button
          className={`drill-button ${isDrillUpDisabled ? "disabled" : ""}`}
          onClick={() => handleDrill("up")}
          disabled={isDrillUpDisabled}
        >
          <ArrowUpwardIcon />
        </button>
        <button
          className={`drill-button ${isDrillDownDisabled ? "disabled" : ""}`}
          onClick={() => handleDrill("down")}
          disabled={isDrillDownDisabled}
        >
          <ArrowDownwardIcon />
        </button>
      </div>
    </>
  );
};

export default DrillControl;
